import React, { useEffect, useState } from "react";
import client from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCcw, Brain, TrendingUp, TrendingDown, Pause, ExternalLink, Loader2 } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [opps, setOpps] = useState({ x1M: [], x1000: [], x100: [], x10: [] });
  const [signals, setSignals] = useState([]);
  const [summary, setSummary] = useState({ open_positions: 0, closed_positions: 0, total_invested: 0, realized_pnl: 0 });
  const [positions, setPositions] = useState([]);
  const [tab, setTab] = useState("x1M");
  const [refreshing, setRefreshing] = useState(false);
  const [signalLoading, setSignalLoading] = useState(null);

  const load = async () => {
    setRefreshing(true);
    try {
      const [o, sg, sm, pos] = await Promise.all([
        client.get("/market/opportunities"),
        client.get("/ai/recent-signals?limit=8"),
        client.get("/portfolio/summary"),
        client.get("/portfolio/positions"),
      ]);
      setOpps(o.data || {});
      setSignals(sg.data || []);
      setSummary(sm.data || {});
      setPositions(pos.data || []);
    } catch (e) { console.error(e); }
    finally { setRefreshing(false); }
  };

  useEffect(() => { load(); const i = setInterval(load, 45000); return () => clearInterval(i); }, []);

  const generateSignal = async (tok) => {
    setSignalLoading(tok.address);
    try {
      const { data } = await client.post("/ai/signal", { token_symbol: tok.symbol, token_address: tok.address, chain: tok.chain });
      setSignals((s) => [data, ...s].slice(0, 8));
      toast.success(`AI: ${data.action} ${tok.symbol} @ ${(data.confidence*100).toFixed(0)}%`);
    } catch (e) {
      toast.error("AI signal failed");
    } finally { setSignalLoading(null); }
  };

  const openPos = async (tok) => {
    if (!user?.active_programs?.length) {
      toast.error("Activate a program first");
      nav("/programs");
      return;
    }
    try {
      await client.post("/portfolio/positions", {
        token_symbol: tok.symbol,
        token_address: tok.address,
        chain: tok.chain,
        amount_usd: 100,
        entry_price: tok.price_usd,
        program: user.active_programs.includes("memecoins") ? "memecoins" : user.active_programs[0],
      });
      toast.success(`Position opened on ${tok.symbol}`);
      load();
    } catch (e) { toast.error("Failed to open position"); }
  };

  const closePos = async (id) => {
    try {
      await client.post(`/portfolio/positions/${id}/close`);
      toast.success("Position closed");
      load();
    } catch (e) { toast.error("Failed to close"); }
  };

  const list = opps[tab] || [];

  return (
    <div data-testid="dashboard-page" className="max-w-[1400px] mx-auto px-6 py-10">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#CCFF00] font-mono">Terminal</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-1">{t("dashboard_title")}</h1>
        </div>
        <button data-testid="dashboard-refresh-button" onClick={load} className="btn-ghost !py-2 !px-3 !text-xs flex items-center gap-2">
          {refreshing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCcw size={14} />} {t("refresh") || "Refresh"}
        </button>
      </div>

      {/* Summary widgets */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[#222] border border-[#222] mb-10">
        <Stat k={t("open_positions")} v={summary.open_positions} />
        <Stat k={t("closed_positions")} v={summary.closed_positions} />
        <Stat k={t("invested")} v={`$${summary.total_invested.toLocaleString()}`} />
        <Stat k={t("realized_pnl")} v={`$${summary.realized_pnl.toLocaleString()}`} accent={summary.realized_pnl >= 0 ? "good" : "bad"} />
        <Stat k="Fees paid" v={`$${(summary.total_commission_paid || 0).toLocaleString()}`} />
      </div>

      <div className="grid grid-cols-12 gap-px bg-[#222] border border-[#222]">
        {/* Opportunities */}
        <div className="col-span-12 lg:col-span-8 bg-[#0A0A0A]">
          <div className="px-6 py-4 border-b border-[#222] flex items-center justify-between">
            <div className="font-display text-sm uppercase tracking-widest">{t("opportunities")}</div>
            <div className="flex gap-1">
              {["x1M", "x1000", "x100", "x10"].map(k => (
                <button key={k} onClick={() => setTab(k)} data-testid={`opps-tab-${k}`}
                  className={`px-3 py-1 text-[10px] font-mono ${tab === k ? "bg-[#CCFF00] text-black" : "border border-[#222] text-white/60 hover:border-[#CCFF00]"}`}>
                  {t(`tier_${k}`) || k}
                </button>
              ))}
            </div>
          </div>
          <div className="max-h-[600px] overflow-y-auto">
            {list.length === 0 && <div className="p-8 text-center text-white/40 text-sm font-mono">{t("no_tokens_bracket") || "No tokens in this bracket right now"}</div>}
            {list.map((tok, i) => (
              <div key={`${tok.chain}-${tok.address}-${i}`} className="px-4 md:px-6 py-4 border-b border-[#222] last:border-0 hover:bg-[#141414] grid grid-cols-12 items-center gap-2" data-testid={`opp-row-${tok.symbol}`}>
                <div className="col-span-7 md:col-span-3">
                  <div className="font-display font-semibold text-sm flex items-center gap-2 flex-wrap">
                    {tok.symbol}
                    <span className="text-[9px] border border-[#333] text-white/50 px-1.5 py-0.5 font-mono uppercase">{tok.chain}</span>
                    {tok.source === "pumpfun" && <span className="text-[9px] bg-[#FF3366]/20 text-[#FF3366] px-1.5 py-0.5 font-mono">PUMP</span>}
                  </div>
                  <div className="text-[10px] text-white/40 truncate">{tok.name}</div>
                </div>
                <div className="col-span-3 md:col-span-2 font-mono text-xs text-right">${Number(tok.price_usd).toFixed(8)}</div>
                <div className="hidden md:block col-span-2 font-mono text-xs text-right text-white/60">${Math.round(tok.market_cap).toLocaleString()}</div>
                <div className={`col-span-2 font-mono text-xs text-right ${tok.price_change_1h >= 0 ? "text-[#CCFF00]" : "text-[#FF3366]"}`}>
                  {tok.price_change_1h >= 0 ? "+" : ""}{Number(tok.price_change_1h).toFixed(1)}%
                </div>
                <div className="col-span-12 md:col-span-3 flex justify-end gap-1">
                  <button data-testid={`signal-btn-${tok.symbol}`} onClick={() => generateSignal(tok)} disabled={signalLoading === tok.address}
                    className="px-2 py-1.5 border border-[#222] hover:border-[#CCFF00] text-[10px] font-mono flex items-center gap-1">
                    {signalLoading === tok.address ? <Loader2 size={10} className="animate-spin" /> : <Brain size={10} />} AI
                  </button>
                  <button data-testid={`open-pos-${tok.symbol}`} onClick={() => openPos(tok)}
                    className="px-2 py-1.5 bg-[#CCFF00] text-black text-[10px] font-mono font-semibold hover:bg-[#B3E600]">BUY $100</button>
                  {tok.dex_url && <a href={tok.dex_url} target="_blank" rel="noreferrer" className="px-2 py-1.5 border border-[#222] hover:border-[#CCFF00]"><ExternalLink size={10} /></a>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* AI Signals */}
        <div className="col-span-12 lg:col-span-4 bg-[#0A0A0A]">
          <div className="px-6 py-4 border-b border-[#222] font-display text-sm uppercase tracking-widest flex items-center gap-2">
            <Brain size={14} className="text-[#CCFF00]" /> {t("ai_signals")}
          </div>
          <div className="max-h-[600px] overflow-y-auto">
            {signals.length === 0 && <div className="p-8 text-center text-white/40 text-sm font-mono">Click {String.fromCharCode(34)}AI{String.fromCharCode(34)} on any token to generate a signal</div>}
            {signals.map((s, i) => (
              <div key={i} className="px-6 py-4 border-b border-[#222] last:border-0" data-testid={`signal-${i}`}>
                <div className="flex items-center justify-between">
                  <div className="font-display font-semibold">{s.token_symbol}</div>
                  <SignalBadge action={s.action} />
                </div>
                <div className="mt-2 flex justify-between text-[10px] font-mono">
                  <span className="text-white/40">{t("confidence")}: <span className="text-[#CCFF00]">{(s.confidence * 100).toFixed(0)}%</span></span>
                  <span className="text-white/40">{t("risk")}: <span className={s.risk_level === "EXTREME" ? "text-[#FF3366]" : "text-white"}>{s.risk_level}</span></span>
                </div>
                <div className="mt-2 text-[11px] text-white/60 leading-relaxed">{s.reasoning}</div>
                {t(`tier_${s.target_multiplier}`) !== `tier_${s.target_multiplier}` && (
                  <div className="mt-2 text-[10px] font-mono text-[#CCFF00]">{t("multiplier")}: {t(`tier_${s.target_multiplier}`)}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Positions */}
      <div className="mt-10 surface">
        <div className="px-6 py-4 border-b border-[#222] font-display text-sm uppercase tracking-widest">{t("portfolio")}</div>
        {positions.length === 0 && <div className="p-8 text-center text-white/40 text-sm font-mono">No positions yet — click BUY on any token</div>}
        {positions.map((p) => (
          <div key={p.id} className="px-4 md:px-6 py-4 border-b border-[#222] last:border-0 grid grid-cols-12 items-center gap-2" data-testid={`pos-${p.id}`}>
            <div className="col-span-6 md:col-span-2 font-display font-semibold flex items-center gap-2 flex-wrap">
              {p.token_symbol}
              {p.auto && <span className="text-[8px] bg-[#CCFF00]/15 text-[#CCFF00] px-1.5 py-0.5 font-mono font-bold">AUTO</span>}
            </div>
            <div className="hidden md:block col-span-1 font-mono text-xs text-white/60">{p.chain.toUpperCase()}</div>
            <div className="col-span-3 md:col-span-2 font-mono text-xs">${p.amount_usd}</div>
            <div className="hidden md:block col-span-2 font-mono text-xs">${Number(p.entry_price).toFixed(8)}</div>
            <div className={`col-span-3 md:col-span-2 font-mono text-xs ${p.pnl_pct >= 0 ? "text-[#CCFF00]" : "text-[#FF3366]"}`}>
              {p.pnl_pct >= 0 ? "+" : ""}{Number(p.pnl_pct).toFixed(2)}% {p.commission_usd > 0 && <span className="hidden md:inline text-white/40 ml-1">(net of {p.commission_pct}%)</span>}
            </div>
            <div className="hidden md:block col-span-2 font-mono text-[10px] text-white/40">
              {p.status === "closed" && p.commission_usd > 0 ? `fee -$${Number(p.commission_usd).toFixed(2)}` : ""}
            </div>
            <div className="col-span-12 md:col-span-1 text-right">
              {p.status === "open" ? (
                <button data-testid={`close-pos-${p.id}`} onClick={() => closePos(p.id)} className="px-3 py-1.5 border border-[#FF3366]/40 text-[#FF3366] text-[10px] font-mono hover:bg-[#FF3366]/10">
                  {t("close_position")}
                </button>
              ) : (
                <span className="text-[10px] font-mono text-white/40">{(p.close_reason || "closed").toUpperCase().replace("_", "-")}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({ k, v, accent }) {
  const cls = accent === "good" ? "text-[#CCFF00]" : accent === "bad" ? "text-[#FF3366]" : "text-white";
  return (
    <div className="bg-[#0A0A0A] p-6">
      <div className="text-[10px] uppercase tracking-widest text-white/40 font-mono">{k}</div>
      <div className={`font-display text-2xl font-bold mt-2 ${cls}`}>{v}</div>
    </div>
  );
}

function SignalBadge({ action }) {
  const map = {
    BUY: { c: "bg-[#CCFF00] text-black", I: TrendingUp },
    SELL: { c: "bg-[#FF3366] text-white", I: TrendingDown },
    HOLD: { c: "bg-[#222] text-white", I: Pause },
  };
  const { c, I } = map[action] || map.HOLD;
  return <span className={`px-2 py-1 text-[10px] font-mono font-bold uppercase ${c} flex items-center gap-1`}><I size={10} /> {action}</span>;
}
